import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import ErrorPage from './../../../pages/404Page';
import SEO from './../../SEO/SEO';
import { IMAGES_BASE_URL } from '../../../config/constants';
import MoreButtonArticle from '../../MoreButton/MoreButtonArticle';
import WorkProjectSkeleton from './WorkProjectSkeleton';
import { useCachedFetch } from './../../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import './WorkProject.scss';

const normalizeForComparison = (text) => {
    if (!text) return '';
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '')
        .trim();
};

const generateSlug = (title) => {
    if (!title) return '';
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
};

const processWorkData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.map(item => {
        const titleText = typeof item.mainTitle === 'object' 
            ? item.mainTitle.en || Object.values(item.mainTitle)[0] 
            : item.mainTitle;
        
        const slug = generateSlug(titleText);
        
        return {
            ...item,
            button: {
                text: {
                    ua: 'Переглянути проект',
                    en: 'View Project',
                    ru: 'Посмотреть проект',
                    pl: 'Zobacz projekt',
                    de: 'Projekt ansehen',
                    es: 'Ver proyecto',
                },
                link: `/work/${slug}`,
            }
        };
    });
};

const WorkContentRenderer = React.memo(({ 
    project, 
    currentLang, 
    mainContent,
    hasContent,
    hasBlocksContent,
    getLocalizedText
}) => {
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const [maxScroll, setMaxScroll] = useState(0);

    const updateScroll = useCallback(() => {
        if (imgRef.current && containerRef.current) {
            const imgHeight = imgRef.current.clientHeight;
            const containerHeight = containerRef.current.clientHeight;
            setMaxScroll(Math.max(imgHeight - containerHeight, 0));
        }
    }, []);

    useEffect(() => {
        let scrollTop = 0;
        let direction = 1;
        let animationId;

        const animateScroll = () => {
            if (imgRef.current && maxScroll > 0) {
                scrollTop += direction * 3;
                if (scrollTop >= maxScroll || scrollTop <= 0) {
                    direction *= -1;
                    scrollTop = Math.min(Math.max(scrollTop, 0), maxScroll);
                }
                imgRef.current.style.top = -scrollTop + 'px';
            }
            animationId = requestAnimationFrame(animateScroll);
        };

        updateScroll();
        animateScroll();

        window.addEventListener('resize', updateScroll);
        return () => {
            cancelAnimationFrame(animationId);
            window.removeEventListener('resize', updateScroll);
        };
    }, [maxScroll, updateScroll]);

    const renderScrollImageSection = useCallback(() => {
        if (!project.scrollImage) return null;

        return (
            <div className='screen__wrapper'>
                <div className='screen__wrapper__head'>
                    <div className='screen__wrapper__head__nav'>
                        <div className='screen__wrapper__head__nav__circle'></div>
                        <div className='screen__wrapper__head__nav__circle'></div>
                        <div className='screen__wrapper__head__nav__circle'></div>
                    </div>
                </div>
                <div className='screen__wrapper__body' ref={containerRef}>
                    <img
                        src={`${IMAGES_BASE_URL}${getLocalizedText(project.scrollImage)}`}
                        alt={getLocalizedText(project.mainTitle) || 'Project screenshot'}
                        className="screen__wrapper__body__pic"
                        ref={imgRef}
                        onLoad={updateScroll}
                        loading="lazy"
                    />
                </div>
            </div>
        );
    }, [project.scrollImage, project.mainTitle, getLocalizedText, updateScroll]);

    const renderProcessSection = useCallback(() => {
        if (!project.processProject || !hasBlocksContent(project.processProject.stages)) return null;

        return (
            <div className='project__info__wrapper__process'>
                {project.processProject.stages.map((block, index) => {
                    const uniqueKey = `process-${block.type}-${index}`;
                    const localizedContent = block.content?.[currentLang] || block.content?.en || '';
                    const localizedAlt = block.alt?.[currentLang] || block.alt?.en || '';

                    switch (block.type) {
                        case 'title':
                            return (
                                <div key={uniqueKey} className='project__info__wrapper__process__item'>
                                    <h4 className='project__info__wrapper__process__title'>
                                        {localizedContent}
                                    </h4>
                                </div>
                            );
                        case 'paragraph':
                            return (
                                <div key={uniqueKey} className='project__info__wrapper__process__item'>
                                    <p className='project__info__wrapper__process__text'>
                                        {localizedContent}
                                    </p>
                                </div>
                            );
                        case 'image':
                            let processImageClass = 'process__wrapper__picture__img';
                            let wrapperClass = 'process__wrapper__picture';
                            
                            if (block.imageType === 'design') {
                                processImageClass = 'design__wrapper__picture__img';
                                wrapperClass = 'design__wrapper__picture';
                                
                                return (
                                    <div key={uniqueKey} className={wrapperClass}>
                                        <img
                                            src={`${IMAGES_BASE_URL}${block.src}`}
                                            alt={localizedAlt}
                                            className={processImageClass}
                                            loading="lazy"
                                        />
                                    </div>
                                );
                            } else {
                                return (
                                    <div key={uniqueKey} className={wrapperClass}>
                                        <img
                                            src={`${IMAGES_BASE_URL}${block.src}`}
                                            alt={localizedAlt}
                                            className={processImageClass}
                                            loading="lazy"
                                        />
                                        {(hasContent(project.mainTitle) || hasContent(project.slogan)) && (
                                            <div className='fixed__slogan'>
                                                {hasContent(project.mainTitle) && (
                                                    <h6 className='fixed__slogan__title'>{getLocalizedText(project.mainTitle)}</h6>
                                                )}
                                                {hasContent(project.slogan) && (
                                                    <p className='fixed__slogan__text'>{getLocalizedText(project.slogan)}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                        default:
                            return null;
                    }
                })}
            </div>
        );
    }, [project.processProject, project.mainTitle, project.slogan, currentLang, hasContent, hasBlocksContent, getLocalizedText]);

    const renderGoalsSection = useCallback(() => {
        if (!project.goalsProject || !hasContent(project.goalsProject.title) || !hasBlocksContent(project.goalsProject.items)) {
            return null;
        }

        return (
            <div className='project__info__wrapper__goals'>
                <h4 className='project__info__wrapper__goals__title'>
                    {getLocalizedText(project.goalsProject.title)}
                </h4>
                <div className='project__info__wrapper__goals__wrapper'>
                    {(() => {
                        const items = project.goalsProject.items || [];
                        const goalsElements = [];

                        for (let i = 0; i < items.length; i += 2) {
                            const subtitleBlock = items[i];
                            const paragraphBlock = items[i + 1];

                            if (subtitleBlock && subtitleBlock.type === 'subtitle' &&
                                paragraphBlock && paragraphBlock.type === 'paragraph') {

                                goalsElements.push(
                                    <div key={`goals-item-${i}`} className='project__info__wrapper__goals__item'>
                                        <h5 className='project__info__wrapper__goals__item__title'>
                                            {subtitleBlock.content?.[currentLang] || subtitleBlock.content?.en || ''}
                                        </h5>
                                        <p className='project__info__wrapper__goals__item__text'>
                                            {paragraphBlock.content?.[currentLang] || paragraphBlock.content?.en || ''}
                                        </p>
                                    </div>
                                );
                            }
                        }

                        return goalsElements;
                    })()}
                </div>
            </div>
        );
    }, [project.goalsProject, currentLang, hasContent, hasBlocksContent, getLocalizedText]);

    const renderBrandSection = useCallback(() => {
        if (!project.brand || !hasContent(project.brand.title) || !hasBlocksContent(project.brand.content?.blocks)) {
            return null;
        }

        return (
            <div className='logo__brand__project__wrapper'>
                <div className='logo__brand__project__wrapper__item'>
                    <h5 className='logo__brand__project__wrapper__item__title'>
                        {getLocalizedText(project.brand.title)}
                    </h5>
                    {project.brand.content.blocks.map((block, index) => {
                        const localizedContent = block.content?.[currentLang] || block.content?.en || '';
                        const localizedAlt = block.alt?.[currentLang] || block.alt?.en || '';
                        const uniqueKey = `brand-${block.type}-${index}`;

                        switch (block.type) {
                            case 'paragraph':
                                return (
                                    <p key={uniqueKey} className='logo__brand__project__wrapper__item__text'>
                                        {localizedContent}
                                    </p>
                                );
                            case 'image':
                                let imageClass = 'logo__brand__wrapper__picture__imgLogo';
                                if (block.imageType === 'brand') {
                                    imageClass = 'logo__brand__wrapper__picture__img';
                                }
                                
                                return (
                                    <img
                                        key={uniqueKey}
                                        src={`${IMAGES_BASE_URL}${block.src}`}
                                        alt={localizedAlt}
                                        className={imageClass}
                                        loading="lazy"
                                    />
                                );
                            default:
                                return null;
                        }
                    })}
                </div>
            </div>
        );
    }, [project.brand, currentLang, hasContent, hasBlocksContent, getLocalizedText]);

    const renderMainContentSection = useCallback(() => {
        if (!project.contentProject || !hasBlocksContent(project.contentProject.blocks)) return null;

        return (
            <div className='project__info__wrapper__main'>
                {project.contentProject.blocks.map((block, index) => {
                    const localizedContent = block.content?.[currentLang] || block.content?.en || '';
                    const localizedAlt = block.alt?.[currentLang] || block.alt?.en || '';
                    const uniqueKey = `main-${block.type}-${index}`;

                    switch (block.type) {
                        case 'title':
                            return (
                                <h4 key={uniqueKey} className='project__info__wrapper__main__title'>
                                    {localizedContent}
                                </h4>
                            );
                        case 'paragraph':
                            return (
                                <p key={uniqueKey} className='project__info__wrapper__main__text'>
                                    {localizedContent}
                                </p>
                            );
                        case 'list':
                            return (
                                <ul key={uniqueKey} className="project__list">
                                    {block.items?.map((item, i) => (
                                        <li key={`${uniqueKey}-item-${i}`} className="project__list__item">
                                            {item[currentLang] || item.en || ''}
                                        </li>
                                    ))}
                                </ul>
                            );
                        case 'image':
                            return (
                                <img
                                    key={uniqueKey}
                                    src={`${IMAGES_BASE_URL}${block.src}`}
                                    alt={localizedAlt}
                                    className='process__wrapper__picture__img'
                                    loading="lazy"
                                />
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        );
    }, [project.contentProject, currentLang, hasBlocksContent]);

    const renderHeroSection = useCallback(() => {
        if (!project.bgImage) return null;

        return (
            <div className='work__project__info__wrapper'>
                <div className='image__work__project__info__item' style={{ backgroundImage: `url(${IMAGES_BASE_URL}${getLocalizedText(project.bgImage)})` }}>
                    <div className='recent__work__substrate__gradient'></div>
                    <div className='image__work__project__info__item__cont'>
                        {hasContent(project.accent) && (
                            <p className='image__work__project__info__item__accent'>{getLocalizedText(project.accent)}</p>
                        )}
                        {hasContent(project.mainTitle) && (
                            <AnimatedOnScroll animation="fade-down" delay={0}>
                            <h2 className='image__work__project__info__item__title'>{getLocalizedText(project.mainTitle)}</h2>
                            </AnimatedOnScroll>
                        )}
                        {project.webLink?.link && hasContent(project.webLink?.text) && (
                            <a href={getLocalizedText(project.webLink.link)} className='image__work__project__info__item__link' target='blank' rel="noopener noreferrer">
                                {getLocalizedText(project.webLink.text)}
                                {project.webLink?.icon && (
                                    <FontAwesomeIcon className='icon__link' icon={faArrowUpRightFromSquare} />
                                )}
                            </a>
                        )}
                        <MoreButtonArticle />
                    </div>
                </div>

                {(project.workList || hasContent(project.overview)) && (
                    <div className='description__work__project__wrapper'>
                        {project.workList?.type === 'list' && project.workList.items && project.workList.items.length > 0 && (
                            <div className='description__work__project__wrapper__left'>
                                <AnimatedOnScroll animation="fade-up" delay={100}>
                                <h3 className='description__work__project__wrapper__title'>
                                    {getLocalizedText(mainContent.contentAccentOne)}
                                </h3>
                                <ul className="project__list">
                                    {project.workList.items.map((item, i) => (
                                        <li key={i} className="project__list__item">
                                            {getLocalizedText(item)}
                                        </li>
                                    ))}
                                </ul>
                                </AnimatedOnScroll>
                            </div>
                        )}
                        {hasContent(project.overview) && (
                            <div className='description__work__project__wrapper__right'>
                                <AnimatedOnScroll animation="fade-up" delay={100}>
                                <h3 className='description__work__project__wrapper__title'>
                                    {getLocalizedText(mainContent.contentAccentTwo)}
                                </h3>
                                <h5 className="project__item__subtitle">{getLocalizedText(project.overview)}</h5>
                                </AnimatedOnScroll>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }, [project, mainContent, hasContent, getLocalizedText]);

    return (
        <>
            {renderHeroSection()}
            
            <div className='project__info__section__wrapper'>
                {renderMainContentSection()}
                {renderGoalsSection()}
                {renderScrollImageSection()}
                {renderBrandSection()}
                {renderProcessSection()}
            </div>
        </>
    );
});

const WorkProject = ({ hideHeaderFooterSetter }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { mainTitle } = useParams();
    const navigate = useNavigate();
    
    const { data: mainData, loading: mainLoading, error: mainError } = useCachedFetch('/data/locales/mainData.json');
    const { data: workData, loading: workLoading, error: workError } = useCachedFetch('/data/locales/work.json');

    const [project, setProject] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const getLocalizedText = useCallback((text) => {
        if (!text) return '';
        if (typeof text === 'object') {
            return text[currentLang] || text.en || '';
        }
        return text;
    }, [currentLang]);

    const hasContent = useCallback((item) => {
        if (!item) return false;
        if (typeof item === 'string') return item.trim() !== '';
        if (typeof item === 'object') {
            const text = item[currentLang] || item.en || '';
            return text.trim() !== '';
        }
        return false;
    }, [currentLang]);

    const hasBlocksContent = useCallback((blocks) => {
        if (!blocks || !Array.isArray(blocks)) return false;
        return blocks.some(block => {
            if (block.type === 'list') {
                return block.items && block.items.length > 0;
            }
            if (block.type === 'image') {
                return block.src;
            }
            return hasContent(block.content);
        });
    }, [hasContent]);

    const seoData = useMemo(() => {
        if (!project?.seo) return null;
        
        const seo = project.seo;
        const title = seo.title?.[currentLang] || seo.title?.en || getLocalizedText(project.mainTitle) || 'Проєкт | Carabinex';
        const description = seo.description?.[currentLang] || seo.description?.en || '';
        const keywords = seo.keywords?.[currentLang] || seo.keywords?.en || '';
        const type = seo.type || 'product';
        const robots = seo.robots || 'index, follow';
        
        const robotsParts = robots.split(', ');
        const noIndex = robotsParts.includes('noindex');
        const noFollow = robotsParts.includes('nofollow');
        
        return { title, description, keywords, type, noIndex, noFollow };
    }, [project, currentLang, getLocalizedText]);

    useEffect(() => {
        if (mainLoading || workLoading) return;

        if (mainError || workError) {
            console.error('Error loading work project data:', mainError || workError);
            return;
        }

        if (!workData?.WorkData || !Array.isArray(workData.WorkData)) {
            console.error('Invalid work data structure');
            return;
        }

        const processedData = processWorkData(workData.WorkData);
        
        const normalizedParam = normalizeForComparison(mainTitle);
        const foundProject = processedData.find(item => {
            const normalizedTitles = [];

            if (item.mainTitle && typeof item.mainTitle === 'object') {
                Object.values(item.mainTitle).forEach(title => {
                    normalizedTitles.push(normalizeForComparison(title));
                });
            } else if (item.mainTitle) {
                normalizedTitles.push(normalizeForComparison(item.mainTitle));
            }

            return normalizedTitles.includes(normalizedParam);
        });

        if (foundProject) {
            setProject(foundProject);
            setCurrentIndex(processedData.indexOf(foundProject));
        } else {
            setProject(null);
        }
    }, [mainTitle, workData, mainLoading, workLoading, mainError, workError]);

    useEffect(() => {
        if (hideHeaderFooterSetter) hideHeaderFooterSetter(project === null);
        return () => {
            if (hideHeaderFooterSetter) hideHeaderFooterSetter(false);
        };
    }, [project, hideHeaderFooterSetter]);

    const handleNextProject = useCallback(() => {
        if (!workData?.WorkData) return;
        
        const processedData = processWorkData(workData.WorkData);
        const nextProjectIndex = (currentIndex + 1) % processedData.length;
        const nextProject = processedData[nextProjectIndex];
        const nextSlug = generateSlug(
            typeof nextProject.mainTitle === 'object'
                ? nextProject.mainTitle[currentLang] || nextProject.mainTitle.en
                : nextProject.mainTitle
        );
        navigate(`/work/${nextSlug}`);
    }, [currentIndex, workData, currentLang, navigate]);

    const handlePreviousProject = useCallback(() => {
        if (!workData?.WorkData) return;
        
        const processedData = processWorkData(workData.WorkData);
        const prevProjectIndex = (currentIndex - 1 + processedData.length) % processedData.length;
        const prevProject = processedData[prevProjectIndex];
        const prevSlug = generateSlug(
            typeof prevProject.mainTitle === 'object'
                ? prevProject.mainTitle[currentLang] || prevProject.mainTitle.en
                : prevProject.mainTitle
        );
        navigate(`/work/${prevSlug}`);
    }, [currentIndex, workData, currentLang, navigate]);

    const nextProjectData = useMemo(() => {
        if (!workData?.WorkData) return { project: null, title: '' };
        
        const processedData = processWorkData(workData.WorkData);
        const nextProject = processedData[(currentIndex + 1) % processedData.length];
        return {
            project: nextProject,
            title: getLocalizedText(nextProject?.mainTitle) || ''
        };
    }, [workData, currentIndex, getLocalizedText]);

    const isLoading = mainLoading || workLoading;
    const mainContent = mainData?.TopMainData?.WorkProjectPage;

    if (isLoading) {
        return <WorkProjectSkeleton />;
    }

    if (!project && !isLoading) {
        return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} />;
    }

    if (!project || !mainContent) {
        return null;
    }

    return (
        <>
            {seoData && (
                <SEO
                    title={seoData.title}
                    description={seoData.description}
                    keywords={seoData.keywords}
                    type={seoData.type}
                    noIndex={seoData.noIndex}
                    noFollow={seoData.noFollow}
                />
            )}

            <div className='work__project__wrapper'>
                <div className='all__link'>
                    <Link to={getLocalizedText(mainContent.buttonAllWork.link)} className='all__link__text'>
                        {getLocalizedText(mainContent.buttonAllWork.text)}
                        <img src={`${IMAGES_BASE_URL}${getLocalizedText(mainContent.buttonAllWork.image)}`} alt="all__icon" className="all__link__icon" loading="lazy" />
                    </Link>
                </div>
                <div className='all__route'>
                    <button className='all__link__next' onClick={handleNextProject}>
                        {getLocalizedText(mainContent.buttonsNav.text1)} <FontAwesomeIcon className="all__arrow__next" icon={faChevronDown} />
                    </button>
                    <button className='all__link__prev' onClick={handlePreviousProject}>
                        {getLocalizedText(mainContent.buttonsNav.text2)} <FontAwesomeIcon className="all__arrow__prev" icon={faChevronDown} />
                    </button>
                </div>

                <WorkContentRenderer
                    project={project}
                    currentLang={currentLang}
                    mainContent={mainContent}
                    hasContent={hasContent}
                    hasBlocksContent={hasBlocksContent}
                    getLocalizedText={getLocalizedText}
                />
            </div>

            {project.feedback &&
                project.feedback.personImage &&
                hasContent(project.feedback.personName) &&
                project.feedback.feedbackLogoImage &&
                hasContent(project.feedback.personCompany) &&
                hasContent(project.feedback.personPosition) &&
                hasContent(project.feedback.personFeedback) && (
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                    <div className='work__project__feedback__wrapper'>
                        <div className='work__project__feedback__wrapper__item__content'>
                            <div className='work__project__feedback__wrapper__item__img'>
                                <img
                                    src={`${IMAGES_BASE_URL}${getLocalizedText(project.feedback.personImage)}`}
                                    alt={getLocalizedText(project.feedback.personName)}
                                    className='work__project__feedback__wrapper__item__img__pic'
                                    loading="lazy"
                                />
                            </div>
                            <p className='work__project__feedback__wrapper__item__content__text'>
                                {getLocalizedText(project.feedback.personFeedback)}
                            </p>
                            <div className='work__project__feedback__wrapper__item__content__company'>
                                <div className='work__project__feedback__wrapper__item__content__company__img'>
                                    <img
                                        src={`${IMAGES_BASE_URL}${getLocalizedText(project.feedback.feedbackLogoImage)}`}
                                        alt={getLocalizedText(project.feedback.personCompany)}
                                        className='work__project__feedback__wrapper__item__content__company__img__pic'
                                        loading="lazy"
                                    />
                                </div>
                                <div className='work__project__feedback__wrapper__item__content__company__info'>
                                    <h6 className='work__project__feedback__wrapper__item__content__company__info__name'>
                                        {getLocalizedText(project.feedback.personName)}
                                    </h6>
                                    <p className='work__project__feedback__wrapper__item__content__company__info__position'>
                                        {getLocalizedText(project.feedback.personPosition)}, {getLocalizedText(project.feedback.personCompany)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className='work__project__feedback__gradient'></div>
                    </div>
                    </AnimatedOnScroll>
                )}

            {nextProjectData.project && nextProjectData.project.bgImage && (
                <div className='next__work__project' style={{
                    backgroundImage: `url(${IMAGES_BASE_URL}${getLocalizedText(nextProjectData.project.bgImage)})`,
                }}>
                    <div className='next__work__project__gradient'></div>
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                    <p className='next__work__project__subtitle'>{getLocalizedText(mainContent.nextProject)}</p>
                    </AnimatedOnScroll>
                    
                    <button className="next__work__project__link" onClick={handleNextProject}>
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                        <h2 className="next__work__project__title">
                            {nextProjectData.title}
                            <FontAwesomeIcon className="work__project__arrow" icon={faChevronDown} />
                        </h2>
                        </AnimatedOnScroll>
                    </button>
                </div>
            )}
        </>
    );
};

export default React.memo(WorkProject);
export { generateSlug };